import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Users, BarChart3, Calendar, Brain, Shield, Clock, ArrowRight, Kanban, Star, Check, Menu, X, Send, Loader2, ChevronDown, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InstallAppButton } from "@/components/PWAInstall";
import { SiteFooter } from "@/components/SiteFooter";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/utils/constants";

const CITY_BG = "https://static.prod-images.emergentagent.com/jobs/488d4f94-f06c-450b-9416-83b7a331180f/images/760587ee7640c6be4729d85e9bd0f195a390d9c3ad6dfd25aa64a24293b05d75.png";

const FEATURES = [
  { icon: Kanban, title: "Kanban Boards", desc: "Drag-and-drop task management with custom columns and statuses", color: "text-blue-500", bg: "bg-blue-500/10" },
  { icon: Brain, title: "AI Assistant", desc: "Parse tasks from natural language, auto-generate subtasks and priorities", color: "text-purple-500", bg: "bg-purple-500/10" },
  { icon: Users, title: "Real-Time Collaboration", desc: "See teammates live on the board, instant task sync across all users", color: "text-emerald-500", bg: "bg-emerald-500/10" },
  { icon: BarChart3, title: "Dashboard Analytics", desc: "Track productivity with charts, completion rates, and time reports", color: "text-amber-500", bg: "bg-amber-500/10" },
  { icon: Calendar, title: "Calendar & Gantt", desc: "Visualize deadlines on a calendar or plan with interactive Gantt charts", color: "text-pink-500", bg: "bg-pink-500/10" },
  { icon: Shield, title: "Secure & Private", desc: "Per-user data isolation, password-protected sharing, Google OAuth", color: "text-indigo-500", bg: "bg-indigo-500/10" },
  { icon: Clock, title: "Time Tracking", desc: "Built-in timer per task, track hours and generate time reports", color: "text-teal-500", bg: "bg-teal-500/10" },
  { icon: Zap, title: "Smart Notifications", desc: "Email alerts for assignments, due dates, mentions, and weekly digests", color: "text-orange-500", bg: "bg-orange-500/10" },
];

const FREE_FEATURES = ["Up to 50 tasks", "5 custom columns", "Kanban board", "Calendar view", "Task templates", "Due dates & priorities", "Subtasks & categories"];
const PRO_FEATURES = ["Unlimited tasks & columns", "AI Task Assistant", "Unlimited team chat & DMs (3 free messages on Free)", "Gantt chart & Workload views", "Calendar sync (Google, Outlook, Apple)", "Automation rules & email-to-task inbox", "Time tracking & file attachments", "Email notifications & digest", "Password-protected sharing", "Export (CSV/JSON)", "Priority support"];
const BUNDLE_FEATURES = ["Everything in Pro", "Full CRM suite", "Deals pipeline with drag & drop stages", "Contacts & companies management", "Activity log — calls, meetings, follow-ups", "Pipeline & won revenue tracking", "Create board tasks from deals in one click", "App Hub — switch between PM & CRM"];

const LazyBgSection = ({ children, className = "", overlay = "bg-black/65", ...props }) => {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.src = CITY_BG;
    img.onload = () => setLoaded(true);
  }, []);
  return (
    <section ref={ref} className={`relative overflow-hidden ${className}`} style={loaded ? { backgroundImage: `url('${CITY_BG}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: '#0f0f1a' }} {...props}>
      <div className={`absolute inset-0 ${overlay} ${loaded ? 'backdrop-blur-[1px]' : ''} transition-opacity duration-700`} />
      <div className="relative z-10">{children}</div>
    </section>
  );
};

export const LandingPage = ({ onGetStarted }) => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: "", email: "", company: "" });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name.trim() || !demoForm.email.trim()) { toast.error("Name and email are required"); return; }
    setDemoSubmitting(true);
    try {
      await axios.post(`${API}/demo-request`, demoForm);
      setDemoSubmitted(true);
      toast.success("Demo request sent!");
    } catch { toast.error("Failed to submit. Please try again."); }
    setDemoSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero */}
      <LazyBgSection id="hero">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/favicon.png" alt="Opero Smart" className="h-8 w-8 rounded-lg" />
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-white">Opero Smart</h1>
          </div>
          <div className="hidden sm:flex items-center gap-6">
            <a href="#features" className="text-sm text-white/80 hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="text-sm text-white/80 hover:text-white transition-colors">Pricing</a>
            <a href="#roadmap" className="text-sm text-white/80 hover:text-white transition-colors">Roadmap</a>
            <a href="/about" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-about-link">About</a>
            <a href="/contact" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-contact-link">Contact</a>
            <Button onClick={onGetStarted} variant="secondary" size="sm" data-testid="hero-login-btn">Sign In</Button>
          </div>
          <button className="sm:hidden text-white p-1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menu">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-black/80 backdrop-blur-md border-t border-white/10 px-4 py-4 space-y-3">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-white/90 py-2">Features</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-white/90 py-2">Pricing</a>
            <a href="/about" className="block text-white/90 py-2">About</a>
            <a href="/contact" className="block text-white/90 py-2">Contact</a>
            <Button onClick={() => { setMobileMenuOpen(false); onGetStarted(); }} variant="secondary" size="sm" className="w-full">Sign In</Button>
          </div>
        )}

        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-20 sm:pb-32 text-center">
          <Badge className="mb-4 sm:mb-6 bg-white/10 text-white border-white/20 backdrop-blur-md text-xs sm:text-sm">Now with AI-powered task management</Badge>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight tracking-tight">
            Manage projects<br /><span className="bg-gradient-to-r from-sky-400 via-teal-300 to-emerald-300 bg-clip-text text-transparent">smarter, not harder</span>
          </h2>
          <p className="text-base sm:text-lg text-white/75 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
            Opero Smart brings Kanban boards, AI assistance, real-time collaboration, analytics, and a built-in CRM into one clean workspace. Built for teams that ship.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <Button onClick={onGetStarted} size="lg" className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white px-8 text-base w-full sm:w-auto shadow-lg shadow-sky-500/25" data-testid="hero-cta-btn">
              Get Started Free <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button onClick={() => setShowVideoModal(true)} variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 px-8 text-base w-full sm:w-auto" data-testid="hero-watch-demo-btn">
              <Play className="h-5 w-5 mr-2" />Watch 60-sec Demo
            </Button>
            <InstallAppButton variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 px-8 text-base w-full sm:w-auto" />
          </div>
          <p className="text-xs text-white/50 mt-4">No credit card required. Free forever plan available.</p>
        </div>
      </LazyBgSection>

      {/* Social Proof */}
      <section className="py-8 sm:py-12 border-b border-border bg-card/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row flex-wrap justify-center gap-4 sm:gap-8 items-center">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(i => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
            <span className="text-sm text-muted-foreground ml-2">4.8/5 rating</span>
          </div>
          <div className="h-6 w-px bg-border hidden sm:block" />
          <span className="text-sm text-muted-foreground">Trusted by growing teams worldwide</span>
          <div className="h-6 w-px bg-border hidden sm:block" />
          <span className="text-sm text-muted-foreground">20% below competitor pricing</span>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-6 flex flex-wrap justify-center gap-2" data-testid="trusted-by-strip">
          <span className="text-xs text-muted-foreground uppercase tracking-wider self-center mr-2">Built for</span>
          {["Startups", "Agencies", "Dev Teams", "Freelancers", "Marketing Teams", "Consultancies"].map(t => (
            <span key={t} className="text-xs font-medium bg-muted rounded-full px-3 py-1.5 text-muted-foreground">{t}</span>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4">Features</Badge>
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Everything you need to <span className="bg-gradient-to-r from-sky-500 to-teal-500 bg-clip-text text-transparent">ship faster</span></h3>
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">From solo founders to growing teams, Opero Smart adapts to your workflow with powerful features that stay out of your way.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="group p-5 sm:p-6 rounded-2xl border border-border bg-card hover:shadow-xl hover:border-primary/30 hover:-translate-y-1 transition-all duration-300" data-testid={`feature-card-${i}`}>
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${f.bg} flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                <f.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${f.color}`} />
              </div>
              <h4 className="font-semibold mb-1.5 sm:mb-2 text-sm sm:text-base">{f.title}</h4>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product Screenshots */}
      <section className="py-16 sm:py-24 bg-muted/30 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <Badge variant="outline" className="mb-4">Product Tour</Badge>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">See Opero Smart in action</h3>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">Real screenshots from the product — no mockups. This is exactly what your team gets.</p>
          </div>
          <div className="space-y-10 sm:space-y-14" data-testid="screenshots-section">
            {[
              { img: "/screens/board.jpg", title: "Kanban Board", desc: "Drag-and-drop tasks across custom columns with priorities, due dates and AI quick-add.", tag: "Free & Pro" },
              { img: "/screens/dashboard.jpg", title: "Analytics Dashboard", desc: "Completion rates, daily activity, priority distribution and time tracked — at a glance.", tag: "Pro" },
              { img: "/screens/timeline.jpg", title: "Gantt Timeline", desc: "Plan weeks ahead with an interactive timeline showing every task's start and due date.", tag: "Pro" },
              { img: "/screens/crm.jpg", title: "CRM & Deals Pipeline", desc: "Track leads, contacts and companies with a drag-and-drop pipeline — and convert won deals into tasks on your board.", tag: "Business Bundle" },
            ].map((s, i) => (
              <div key={i} className={`flex flex-col ${i % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-6 lg:gap-12`} data-testid={`screenshot-item-${i}`}>
                <div className="lg:w-1/3 text-center lg:text-left">
                  <Badge variant="secondary" className="mb-3">{s.tag}</Badge>
                  <h4 className="text-xl font-bold mb-2">{s.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
                <div className="lg:w-2/3 w-full">
                  <div className="rounded-xl border border-border shadow-xl overflow-hidden bg-card">
                    <div className="flex items-center gap-1.5 px-4 py-2.5 bg-muted/60 border-b border-border">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                      <span className="ml-3 text-[10px] text-muted-foreground bg-background rounded px-2 py-0.5">operosmart.com</span>
                    </div>
                    <img src={s.img} alt={`${s.title} — Opero Smart`} className="w-full h-auto" loading="lazy" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CRM/ERP Demo Video */}
      <section className="py-16 sm:py-24" style={{ background: '#0f0f1a' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-12">
            <Badge className="mb-4 bg-white/10 text-white border-white/20">CRM & ERP</Badge>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-white">From lead to invoice in 60 seconds</h3>
            <p className="text-sm sm:text-base text-white/70 max-w-xl mx-auto">Watch a deal become a quotation, a sales order, a purchase order — and an invoice. All in one workspace, no spreadsheets.</p>
          </div>
          <div className="rounded-xl border border-white/10 shadow-2xl overflow-hidden" data-testid="crm-erp-video-section">
            <video controls preload="none" className="w-full h-auto" poster="/screens/opero-crm-erp-poster.jpg" data-testid="crm-erp-demo-video"
              onPlay={() => { if (!window.__crmVidTracked) { window.__crmVidTracked = true; axios.post(`${API}/track/video-play`, { source: "landing-crm-erp" }).catch(() => {}); } }}>
              <source src="/screens/opero-crm-erp-demo-voiceover.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <Badge variant="outline" className="mb-4">Pricing</Badge>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Simple, transparent pricing</h3>
            <p className="text-sm sm:text-base text-muted-foreground">Start free. Upgrade when your team grows. 20% below competitors.</p>
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => setBillingCycle("monthly")} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billingCycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
              <button onClick={() => setBillingCycle("yearly")} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billingCycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                Yearly <span className="text-xs opacity-75">Save 17%</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {/* Free */}
            <div className="bg-card rounded-2xl border p-6 sm:p-8" data-testid="pricing-free">
              <h4 className="text-lg font-semibold mb-1">Free</h4>
              <p className="text-sm text-muted-foreground mb-4 sm:mb-6">Perfect for personal use</p>
              <div className="mb-4 sm:mb-6">
                <span className="text-3xl sm:text-4xl font-bold">$0</span>
                <span className="text-muted-foreground">/forever</span>
              </div>
              <Button onClick={onGetStarted} variant="outline" className="w-full mb-4 sm:mb-6" data-testid="pricing-free-cta">Get Started</Button>
              <ul className="space-y-2.5 sm:space-y-3">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm"><Check className="h-4 w-4 text-emerald-500 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>

            {/* Pro */}
            <div className="bg-card rounded-2xl border-2 border-primary p-6 sm:p-8 relative shadow-xl shadow-primary/10 md:scale-[1.02]" data-testid="pricing-pro">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">Most Popular</Badge>
              <h4 className="text-lg font-semibold mb-1">Pro</h4>
              <p className="text-sm text-muted-foreground mb-4 sm:mb-6">For teams that move fast</p>
              <div className="mb-2">
                <span className="text-3xl sm:text-4xl font-bold">${billingCycle === "monthly" ? "7.99" : "79"}</span>
                <span className="text-muted-foreground">/{billingCycle === "monthly" ? "month" : "year"}</span>
              </div>
              {billingCycle === "yearly" && <p className="text-xs text-emerald-600 font-medium mb-4">Save $16.88/year vs monthly</p>}
              {billingCycle === "monthly" && <p className="text-xs text-muted-foreground mb-4">&nbsp;</p>}
              <Button onClick={onGetStarted} className="w-full mb-2 bg-primary" data-testid="pricing-pro-cta">Start 14-Day Free Trial</Button>
              <p className="text-xs text-center text-muted-foreground mb-4">No credit card required</p>
              <ul className="space-y-2.5 sm:space-y-3">
                {PRO_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm"><Check className="h-4 w-4 text-primary shrink-0" />{f}</li>
                ))}
              </ul>
            </div>

            {/* Business Bundle */}
            <div className="bg-card rounded-2xl border-2 border-violet-500 p-6 sm:p-8 relative shadow-xl shadow-violet-500/10" data-testid="pricing-bundle">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-500 to-fuchsia-500">Pro + CRM</Badge>
              <h4 className="text-lg font-semibold mb-1">Business Bundle</h4>
              <p className="text-sm text-muted-foreground mb-4 sm:mb-6">Run projects & sales in one place</p>
              <div className="mb-2">
                <span className="text-3xl sm:text-4xl font-bold">$12.99</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-xs text-violet-600 dark:text-violet-400 font-medium mb-4">Unlocks the CRM workspace</p>
              <Button onClick={onGetStarted} className="w-full mb-2 bg-violet-600 hover:bg-violet-700 text-white" data-testid="pricing-bundle-cta">Get the Bundle</Button>
              <p className="text-xs text-center text-muted-foreground mb-4">&nbsp;</p>
              <ul className="space-y-2.5 sm:space-y-3">
                {BUNDLE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm"><Check className="h-4 w-4 text-violet-500 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24 max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Testimonials</Badge>
          <h3 className="text-2xl sm:text-3xl font-bold mb-4">Loved by teams everywhere</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: "Sarah Chen", role: "Product Manager", text: "Opero Smart replaced three tools for our team. The AI task parsing alone saves me 30 minutes every day.", avatar: "SC" },
            { name: "Marcus Rivera", role: "Freelance Developer", text: "The time tracking and Gantt charts are incredibly useful. I finally know exactly where my hours go.", avatar: "MR" },
            { name: "Emily Watson", role: "Startup Founder", text: "Real-time collaboration means my remote team stays perfectly in sync. The pricing is unbeatable too.", avatar: "EW" },
          ].map((t, i) => (
            <div key={i} className="bg-card border rounded-2xl p-6" data-testid={`testimonial-${i}`}>
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(s => <Star key={s} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-500/25 to-teal-500/25 flex items-center justify-center text-xs font-bold text-primary">{t.avatar}</div>
                <div>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Comparison</Badge>
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">How Opero Smart stacks up</h3>
            <p className="text-sm sm:text-base text-muted-foreground">Everything the big tools offer — plus AI, built-in chat and a full CRM — for less.</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-border bg-card" data-testid="comparison-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-medium text-muted-foreground">Feature</th>
                  <th className="p-4 font-bold text-primary">Opero Smart</th>
                  <th className="p-4 font-medium text-muted-foreground">Trello</th>
                  <th className="p-4 font-medium text-muted-foreground">Asana</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Kanban boards", true, true, true],
                  ["AI task assistant", true, false, false],
                  ["Built-in team chat & DMs", true, false, false],
                  ["Gantt / Timeline view", true, "paid", "paid"],
                  ["Time tracking", true, false, "paid"],
                  ["Automation rules", true, "limited", true],
                  ["Email-to-task inbox", true, true, false],
                  ["Built-in CRM & deals pipeline", "bundle", false, false],
                  ["Calendar sync (iCal)", true, "paid", false],
                ].map(([label, ...vals], i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    <td className="p-4 font-medium">{label}</td>
                    {vals.map((v, j) => (
                      <td key={j} className="p-4 text-center">
                        {v === true ? <Check className={`h-4 w-4 mx-auto ${j === 0 ? 'text-primary' : 'text-emerald-500'}`} /> : v === false ? <X className="h-4 w-4 mx-auto text-muted-foreground/40" /> : v === "bundle" ? <span className="inline-flex items-center gap-1 text-xs font-medium text-primary"><Check className="h-4 w-4" />Bundle</span> : <span className="text-xs text-muted-foreground capitalize">{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-muted/40">
                  <td className="p-4 font-semibold">Starting price (per user/mo)</td>
                  <td className="p-4 text-center font-bold text-primary">$7.99</td>
                  <td className="p-4 text-center text-muted-foreground">$12.50</td>
                  <td className="p-4 text-center text-muted-foreground">$10.99</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-3">Competitor pricing based on publicly listed Premium/Starter tiers, June 2026. Trademarks belong to their respective owners.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <img src="https://static.prod-images.emergentagent.com/jobs/488d4f94-f06c-450b-9416-83b7a331180f/images/f8ffc7a1df47f82a29c9a7fc5179b400473a06bb63a487be67ef5dc54c02ab99.png" alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-testid="faq-header-banner" />
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4 border-white/40 text-white">FAQ</Badge>
            <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-white">Frequently asked questions</h3>
          </div>
          <div className="space-y-4">
            {[
              { q: "What is Opero Smart?", a: "Opero Smart is a smart project management tool that combines AI-powered task management, Kanban boards, real-time team collaboration, Gantt charts, and analytics into one platform. It helps teams manage projects smarter, not harder." },
              { q: "Is Opero Smart free to use?", a: "Yes! Opero Smart offers a free forever plan with up to 50 tasks, 5 custom columns, Kanban board, calendar view, and task templates. The Pro plan starts at $7.99/month with a 14-day free trial — no credit card required." },
              { q: "How does Opero Smart compare to Notion, Asana, and Trello?", a: "Opero Smart offers similar features — Kanban boards, task management, team collaboration — but at 20% lower pricing. Plus, Opero Smart includes a built-in AI task assistant, real-time collaboration with live presence, and Gantt charts in the Pro plan." },
              { q: "Does Opero Smart have an AI assistant?", a: "Yes! Opero Smart's AI assistant can parse natural language into structured tasks, auto-generate subtasks, and suggest priorities. Just type 'Call John tomorrow at 3pm' and the AI creates a task with the right due date and details." },
              { q: "Can teams collaborate in real-time?", a: "Absolutely! Opero Smart features real-time collaboration with live presence indicators, instant task sync across all users, and editing indicators so you know when a teammate is working on the same task." },
              { q: "Is my data secure?", a: "Yes. Opero Smart uses per-user data isolation — each user's tasks, projects, and data are completely private. Shared content uses password-protected links with optional expiry dates. Authentication is handled via Google OAuth." },
            ].map((faq, i) => (
              <details key={i} className="group bg-white/10 backdrop-blur-md border border-white/15 rounded-xl" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none font-medium text-sm text-white hover:text-teal-300 transition-colors">
                  {faq.q}
                  <ChevronDown className="h-4 w-4 text-white/60 group-open:rotate-180 transition-transform shrink-0 ml-4" />
                </summary>
                <div className="px-5 pb-5 text-sm text-white/75 leading-relaxed -mt-1">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap Preview */}
      <section id="roadmap" className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Badge variant="outline" className="mb-4">Roadmap</Badge>
          <h3 className="text-2xl sm:text-3xl font-bold mb-4">Always improving</h3>
          <p className="text-sm sm:text-base text-muted-foreground mb-8">See what we're building next. We ship new features every week.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-left">
            {[
              { label: "Shipped", count: "25+", color: "bg-emerald-500" },
              { label: "In Progress", count: "2", color: "bg-amber-500" },
              { label: "Planned", count: "4", color: "bg-indigo-500" },
              { label: "Considering", count: "3", color: "bg-purple-500" },
            ].map((s, i) => (
              <div key={i} className="bg-card border rounded-xl p-4 hover:border-primary/30 hover:shadow-md transition-all">
                <div className={`w-2.5 h-2.5 rounded-full ${s.color} mb-2`} />
                <div className="text-2xl font-bold">{s.count}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA with embedded demo video */}
      <LazyBgSection overlay="bg-black/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            <div className="lg:w-2/5 text-center lg:text-left">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Ready to work smarter?</h3>
              <p className="text-white/70 mb-6 sm:mb-8 text-base sm:text-lg">Join teams that use Opero Smart to ship faster, stay organized, and collaborate in real-time. Watch it in action →</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button onClick={onGetStarted} size="lg" className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white px-8 sm:px-10 text-base w-full sm:w-auto shadow-lg shadow-sky-500/25">
                  Get Started Free <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
                <Button onClick={() => setShowDemoModal(true)} variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 px-8 text-base w-full sm:w-auto" data-testid="hero-demo-btn">
                  <Send className="h-4 w-4 mr-2" />Request Demo
                </Button>
              </div>
            </div>
            <div className="lg:w-3/5 w-full">
              <div className="rounded-xl border border-white/20 shadow-2xl overflow-hidden bg-black" data-testid="cta-video-frame">
                <div className="flex items-center gap-1.5 px-4 py-2.5 bg-white/10 border-b border-white/10">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  <span className="ml-3 text-[10px] text-white/60 bg-white/10 rounded px-2 py-0.5">operosmart.com — 60-sec tour</span>
                </div>
                <video autoPlay muted loop playsInline controls className="w-full h-auto" data-testid="cta-demo-video" poster="/screens/board.jpg">
                  <source src="/videos/how-to-use.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </LazyBgSection>

      {/* Footer */}
      <SiteFooter />

      {/* Demo Request Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="video-demo-modal">
          <DialogHeader className="sr-only"><DialogTitle>Opero Smart 60-second demo</DialogTitle></DialogHeader>
          {showVideoModal && (
            <video controls autoPlay className="w-full h-auto" data-testid="landing-demo-video" poster="/screens/board.jpg"
              onPlay={() => { if (!window.__vidTracked) { window.__vidTracked = true; axios.post(`${API}/track/video-play`, { source: "landing" }).catch(() => {}); } }}>
              <source src="/videos/how-to-use.mp4" type="video/mp4" />
            </video>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDemoModal} onOpenChange={(v) => { setShowDemoModal(v); if (!v) { setDemoSubmitted(false); setDemoForm({ name: "", email: "", company: "" }); } }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-primary" />Request a Demo</DialogTitle>
          </DialogHeader>
          {demoSubmitted ? (
            <div className="text-center py-6" data-testid="demo-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Check className="h-7 w-7 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Request Sent!</h3>
              <p className="text-sm text-muted-foreground mb-4">We'll reach out to you shortly to schedule a personalized demo.</p>
              <Button onClick={() => setShowDemoModal(false)} variant="outline">Close</Button>
            </div>
          ) : (
            <form onSubmit={handleDemoSubmit} className="space-y-4" data-testid="demo-request-form">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Name *</label>
                <Input placeholder="Your name" value={demoForm.name} onChange={(e) => setDemoForm(f => ({ ...f, name: e.target.value }))} data-testid="demo-name" required />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Email *</label>
                <Input type="email" placeholder="you@company.com" value={demoForm.email} onChange={(e) => setDemoForm(f => ({ ...f, email: e.target.value }))} data-testid="demo-email" required />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Company</label>
                <Input placeholder="Your company name" value={demoForm.company} onChange={(e) => setDemoForm(f => ({ ...f, company: e.target.value }))} data-testid="demo-company" />
              </div>
              <Button type="submit" className="w-full" disabled={demoSubmitting} data-testid="demo-submit">
                {demoSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {demoSubmitting ? "Submitting..." : "Request Demo"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">We'll get back to you within 24 hours</p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
